import { createPushLifecycle } from '../utils/pushLifecycle.js';
import { usableVerdict } from '../utils/observations.js';
import { writeStorage, removeStorage } from '../utils/storage.js';
import { useState, useEffect, useCallback, useRef } from 'react';

const API = import.meta.env.VITE_API_BASE || '/api';

// useNotification, manages Web Push subscription lifecycle and in-app
// "GO tonight" toasts when the observing score crosses a threshold.
//
// Returns:
//   permission:    'default' | 'granted' | 'denied' | 'unsupported'
//   subscribed:    bool, true if a push subscription is active
//   requestPush:   fn, call to ask for notification permission + subscribe
//   unsubscribe:   fn, remove the subscription
//   toast:         { message, score, verdict } | null, in-app alert to show
//   dismissToast:  fn

const STORAGE_KEY = 'og.push.endpoint';
const GOOD_THRESHOLD = 60; // score >= this triggers a "GO" toast

// Converts a base64url string to a Uint8Array for applicationServerKey.
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)));
}

export function useNotification({ observer, verdict, activity }) {
  const [permission, setPermission] = useState(() => {
    if (typeof Notification === 'undefined') return 'unsupported';
    return Notification.permission;
  });
  const [subscribed,  setSubscribed]  = useState(false);
  const [error, setError] = useState(null);
  const [pending, setPending] = useState(false);
  const lifecycle = useRef(null);
  if (!lifecycle.current) lifecycle.current = createPushLifecycle({ base: API });
  const context = useRef({ observer, activity });
  context.current = { observer, activity };
  const registerCurrent = useCallback(async sub => {
    for (let attempt = 0; attempt < 4; attempt++) {
      const current = context.current;
      const endpoint = await lifecycle.current.register(sub, current.observer, current.activity);
      if (current === context.current || (current.observer === context.current.observer && current.activity === context.current.activity)) return endpoint;
    }
    throw new Error('Preferences changed while saving notifications. Retry to save your current location.');
  }, []);
  const [toast,       setToast]       = useState(null);
  const prevScoreRef = useRef(null);

  // ── In-app toast: fire when score first crosses GOOD_THRESHOLD ───────────
  useEffect(() => {
    const score = verdict?.score;
    if (!usableVerdict(verdict) || !verdict.notification_eligible) { setToast(null); return; }
    const prev = prevScoreRef.current;
    prevScoreRef.current = score;
    // Crossed upward into good territory (or first reading that's already good)
    if (score >= GOOD_THRESHOLD && (prev == null || prev < GOOD_THRESHOLD)) {
      setToast({
        score,
        verdict: verdict.verdict,
        message: `${verdict.verdict} night, score ${score}. ${verdict.activity_label || 'observing'} conditions are good.`,
      });
    }
  }, [verdict?.data_status, verdict?.notification_eligible, verdict?.score, verdict?.verdict, verdict?.activity_label]);

  // Reconcile existing browser subscriptions on mount and preference changes.
  useEffect(() => {
    if (typeof Notification === 'undefined' || !('serviceWorker' in navigator)) return;
    let cancelled = false;
    setSubscribed(false); setError(null);
    navigator.serviceWorker.register('/sw.js', { scope: '/' })
      .then(reg => reg.pushManager.getSubscription())
      .then(async sub => {
        if (!sub || cancelled) return;
        setPending(true);
        const endpoint = await lifecycle.current.register(sub, observer, activity);
        if (!cancelled) { writeStorage(STORAGE_KEY, endpoint); setSubscribed(true); }
      })
      .catch(e => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setPending(false); });
    return () => { cancelled = true; };
  }, [observer?.lat, observer?.lon, observer?.name, activity]);

  // ── Request permission + subscribe ──────────────────────────────────────
  const requestPush = useCallback(async () => {
    if (typeof Notification === 'undefined' || pending) return;
    if (!observer) { setError('Set your observing location before enabling notifications.'); return; }
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setPermission('unsupported');
      return;
    }

    const perm = await Notification.requestPermission();
    setPermission(perm);
    if (perm !== 'granted') return;

    setPending(true); setError(null);
    try {
      // Register service worker if not already registered
      let reg = await navigator.serviceWorker.getRegistration('/sw.js');
      if (!reg) reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
      await navigator.serviceWorker.ready;

      // Get VAPID public key from server
      const vapidRes = await fetch(`${API}/push/vapid-public`, { signal: AbortSignal.timeout(10000) });
      if (!vapidRes.ok) throw new Error('Notifications are unavailable on this server. Retry later.');
      const { publicKey } = await vapidRes.json();
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });

      const endpoint = await registerCurrent(sub);
      writeStorage(STORAGE_KEY, endpoint);
      setSubscribed(true);
    } catch (e) {
      setSubscribed(false); setError(e.message);
    } finally { setPending(false); }
  }, [observer, activity, pending, registerCurrent]);

  // ── Unsubscribe ──────────────────────────────────────────────────────────
  const unsubscribe = useCallback(async () => {
    if (pending) return;
    setPending(true); setError(null);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) await lifecycle.current.remove(sub);
      removeStorage(STORAGE_KEY);
      setSubscribed(false);
    } catch (e) {
      setError(e.message);
    } finally { setPending(false); }
  }, [pending]);

  const dismissToast = useCallback(() => setToast(null), []);

  return { permission, subscribed, pending, error, requestPush, unsubscribe, toast, dismissToast };
}
